/**
 * Demo seed for the dashboard.
 *
 * Activated by visiting any page with ``?demo=1`` (or ``&demo=1``).
 * Replaces the real REST bootstrap and WebSocket stream with a local
 * scripted stream that pumps the store with fake building data, an
 * atmosphere loop and a pod's worth of analyst chatter -- enough to
 * preview every scene + bubble + tint without waiting for market
 * hours.
 *
 * Lives outside ``state/store`` because the store is meant to stay
 * transport-agnostic.  The demo "transport" only depends on the same
 * ``appendEvent`` / ``setBuilding`` actions a real REST/WS pair would.
 */

import type {
  AnyEnvelope,
  EventActor,
  EventMood,
} from '@/events';
import { EventType } from '@/events';
import type { BuildingState, PodSummary } from '@/api/rest';
import type { DashboardStore } from '@/state/store';

const DEMO_BUILDING: BuildingState = {
  as_of: new Date().toISOString(),
  floors: [
    {
      id: 'canary_lab',
      label: 'Canary Lab',
      pods: [
        pod('sandbox_policy_updater_v3', 'Sandbox Policy v3', 'canary_lab', {
          isCanary: true,
        }),
      ],
    },
    {
      id: 'active_offices',
      label: 'Active Offices',
      pods: [
        pod('deepseek_v3', 'DeepSeek V3 Pod', 'active_offices'),
        pod('gemini31_flash_lite', 'Gemini 3.1 Flash Lite', 'active_offices'),
        pod('gemini_flash', 'Gemini Flash Pod', 'active_offices'),
        pod('gpt4omini', 'GPT-4o-mini Pod', 'active_offices'),
        pod('qwen_plus', 'Qwen Plus Pod', 'active_offices'),
      ],
    },
    {
      id: 'reduced_floor',
      label: 'Reduced Floor',
      pods: [
        pod('grok_fast', 'Grok Fast (reduced)', 'reduced_floor', {
          riskState: 'reduced',
          haircut: 0.4,
        }),
      ],
    },
    {
      id: 'frozen_basement',
      label: 'Frozen Basement',
      pods: [
        pod('llama_405b', 'Llama 405B (frozen)', 'frozen_basement', {
          riskState: 'frozen',
          haircut: 1.0,
        }),
      ],
    },
  ],
  calendar: {
    as_of: new Date().toISOString(),
    market_state: 'open',
    next_session_open_at: null,
    next_session_close_at: new Date(Date.now() + 4 * 3600 * 1000).toISOString(),
    next_daily_close_at: new Date(Date.now() + 4 * 3600 * 1000).toISOString(),
    next_friday_celebration_at: new Date(
      Date.now() + 26 * 3600 * 1000,
    ).toISOString(),
  },
  canary_pods: ['pod_moex_sandbox_policy_updater_v3'],
  policy_updater: { enabled: true, shadow_mode: false, cooldown_days: 7 },
  stats: { active_pods_count: 5, canary_count: 1 },
};

interface PodOpts {
  isCanary?: boolean;
  riskState?: 'active' | 'watch' | 'reduced' | 'frozen';
  haircut?: number;
}

function pod(
  shortId: string,
  name: string,
  floor: 'canary_lab' | 'active_offices' | 'reduced_floor' | 'frozen_basement',
  opts: PodOpts = {},
): PodSummary {
  // Spice the demo pods with realistic-looking daily PnL so the
  // building view tells a story even before events start flowing.
  // Stable per-pod numbers (hash of the shortId) so reloads look
  // consistent.
  const seed = Array.from(shortId).reduce((a, c) => a + c.charCodeAt(0), 0);
  const ret = ((seed % 47) - 23) / 1000; // -2.3 % ... +2.4 %
  const capital = 1_000_000;
  return {
    pod_id: `pod_moex_${shortId}`,
    name,
    is_canary: opts.isCanary ?? false,
    risk_state: opts.riskState ?? 'active',
    haircut_pct: opts.haircut ?? 0,
    risk_reason: null,
    risk_since: null,
    capital,
    floor,
    nav: Math.round(capital * (1 + ret)),
    daily_pnl: Math.round(capital * ret),
    daily_return: ret,
    utility_today: ret * 5, // crude proxy for the demo
    utility_avg_10d: ret * 4,
    current_modifiers:
      (opts.riskState ?? 'active') === 'frozen'
        ? { trading_halt: true, risk_appetite: 'frozen' }
        : (opts.riskState ?? 'active') === 'reduced'
          ? { risk_appetite: 'conservative', inactivity_scrutiny: true }
          : ret < 0
            ? { risk_appetite: 'cautious' }
            : { risk_appetite: 'balanced' },
    last_action:
      (opts.riskState ?? 'active') === 'reduced'
        ? {
            kind: 'applied',
            trigger_rule: 'utility_below_threshold',
            param_changed: 'risk_appetite',
            value_after: 'conservative',
            audit_id: 41 + seed,
            at: new Date(Date.now() - 1800_000).toISOString(),
          }
        : opts.isCanary
          ? {
              kind: 'shadow_recommended',
              trigger_rule: 'canary_experiment',
              param_changed: 'turnover_cap',
              value_after: 0.6,
              audit_id: 80 + seed,
              at: new Date(Date.now() - 600_000).toISOString(),
            }
          : null,
  };
}

const AGENTS: { actor: EventActor; mood: EventMood }[] = [
  { actor: 'qa', mood: 'confident' },
  { actor: 'sa', mood: 'worried' },
  { actor: 'fa', mood: 'thinking' },
  { actor: 'researcher', mood: 'idle' },
  { actor: 'rm', mood: 'idle' },
  { actor: 'ea', mood: 'confident' },
];

const TICKERS = ['SBER', 'GAZP', 'LKOH', 'AFKS', 'YNDX', 'TCSG', 'MGNT', 'PLZL'];

const WEATHER_KINDS = ['sunny', 'cloudy', 'storm', 'windy', 'snow', 'fog'] as const;

let demoStarted = false;

export interface DemoHandle {
  stop: () => void;
}

/**
 * Boot the demo data + scripted event stream into the given store.
 * Idempotent: a second call returns the same handle without restarting.
 */
export function startDemo(store: DashboardStore): DemoHandle {
  if (demoStarted) {
    return { stop: () => {} };
  }
  demoStarted = true;

  store.setConnection('subscribed');
  store.setBuilding(DEMO_BUILDING);

  // Expose the store + envelope helper on ``window`` while demo mode is
  // active so it's trivial to script visual tests from the devtools
  // console (or from automated browser sessions) -- e.g.,
  // ``__demo.freeze('pod_moex_gemini_flash')`` to trigger the ice
  // overlay on demand without waiting for the next cycle.
  (window as unknown as { __demo?: unknown }).__demo = {
    store,
    appendEvent: (e: AnyEnvelope) => store.appendEvent(e),
    /** Manually trigger a session-open warning banner. */
    sessionWarning(kind: 'open' | 'close' = 'open', minutes = 4) {
      store.appendEvent(
        envelope({
          type: EventType.BUILDING_SESSION_WARNING,
          subject: 'building',
          data: {
            boundary_kind: kind,
            boundary_at: new Date(Date.now() + minutes * 60_000).toISOString(),
            minutes_until_boundary: minutes,
            trading_date: '2026-05-19',
            market: 'MOEX',
          },
          sceneHint: { actor: 'system', mood: kind === 'open' ? 'excited' : 'worried' },
        }),
      );
    },
    /** Manually run the daily-close night ritual.
     *  Fires ``daily_close_started`` immediately, then
     *  ``daily_close_completed`` after the supplied delay.  Optional
     *  knobs for the completed-event summary fields. */
    dailyClose(opts: { delayMs?: number; podsClosed?: number; totalPnl?: number; topPodName?: string } = {}) {
      const delayMs = opts.delayMs ?? 6_000;
      store.appendEvent(
        envelope({
          type: EventType.BUILDING_DAILY_CLOSE_STARTED,
          subject: 'building',
          data: { trading_date: '2026-05-18', closing_time: new Date().toISOString() },
          sceneHint: { actor: 'system', mood: 'worried' },
        }),
      );
      window.setTimeout(() => {
        store.appendEvent(
          envelope({
            type: EventType.BUILDING_DAILY_CLOSE_COMPLETED,
            subject: 'building',
            data: {
              trading_date: '2026-05-18',
              pods_closed: opts.podsClosed ?? 5,
              total_pnl: opts.totalPnl ?? 12_345_000,
              top_pod_name: opts.topPodName ?? 'DeepSeek V3 Pod',
            },
            sceneHint: { actor: 'system', mood: 'proud' },
          }),
        );
      }, delayMs);
    },
    /** Manually trigger a ``focus_on_target`` camera directive for
     *  a pod -- spotlights it in the building view for ~2.5 s. */
    focusOn(podId: string) {
      store.appendEvent(
        envelope({
          type: EventType.AGENT_IDLE_ACTION,
          subject: `pod:${podId}`,
          data: { actor: 'system', text: 'spotlight', mood: 'idle', kind: 'demo_focus' },
          sceneHint: {
            actor: 'system',
            mood: 'idle',
            camera: 'focus_on_target',
            intensity: 'high',
          },
        }),
      );
    },
    /** Manually trigger a factor-crowd warning banner. */
    factorCrowd() {
      store.appendEvent(
        envelope({
          type: EventType.BUILDING_FACTOR_CROWD_DETECTED,
          subject: 'building',
          data: {
            as_of: new Date().toISOString(),
            max_correlation: 0.83,
            threshold: 0.7,
            affected_pods: [
              'pod_moex_deepseek_v3',
              'pod_moex_gpt4omini',
              'pod_moex_qwen_plus',
            ],
            window_size: 30,
            pairs_count: 3,
          },
          sceneHint: { actor: 'system', mood: 'worried' },
        }),
      );
    },
    /** Manually trigger a full Phase 9 Friday celebration sequence. */
    celebrate() {
      const podIds = [
        'pod_moex_deepseek_v3',
        'pod_moex_gpt4omini',
        'pod_moex_qwen_plus',
      ];
      const names = ['DeepSeek V3 Pod', 'GPT-4o-mini Pod', 'Qwen Plus Pod'];
      const values = [0.187, 0.094, 0.061];
      store.appendEvent(
        envelope({
          type: EventType.CELEBRATION_WEEKLY_STARTED,
          subject: 'building',
          data: {
            winner_pod_id: podIds[0],
            winner_name: names[0],
            metric: 'utility_avg_7d',
            value: values[0],
            samples: 35,
            podium_count: 3,
          },
        }),
      );
      // Podium pings come in over the next ~1.5 s.
      [1, 2, 3].forEach((rank, idx) => {
        window.setTimeout(() => {
          store.appendEvent(
            envelope({
              type: EventType.CELEBRATION_PODIUM,
              subject: 'building',
              data: {
                rank,
                pod_id: podIds[idx],
                name: names[idx],
                metric: 'utility_avg_7d',
                value: values[idx],
              },
            }),
          );
        }, 300 + idx * 400);
      });
    },
    freeze(podId: string) {
      store.appendEvent(
        envelope({
          type: EventType.RISK_STATE_CHANGED,
          subject: `pod:${podId}`,
          data: {
            from: 'active',
            to: 'frozen',
            haircut_pct: 1.0,
            reason: 'manual_freeze',
          },
          sceneHint: { actor: 'risk_officer', mood: 'frozen', camera: 'focus_on_target', intensity: 'critical' },
        }),
      );
    },
    unfreeze(podId: string) {
      store.appendEvent(
        envelope({
          type: EventType.RISK_STATE_CHANGED,
          subject: `pod:${podId}`,
          data: {
            from: 'frozen',
            to: 'active',
            haircut_pct: 0,
            reason: 'manual_unfreeze',
          },
          sceneHint: { actor: 'risk_officer', mood: 'idle' },
        }),
      );
    },
  };

  let cycle = 0;
  let sunPhaseIdx = 2; // start at noon
  let weatherIdx = 0;
  const sunPhases = [
    'predawn',
    'morning',
    'noon',
    'afternoon',
    'evening',
    'night',
  ] as const;

  // ---------- atmosphere loop ----------
  const sunTimer = window.setInterval(() => {
    sunPhaseIdx = (sunPhaseIdx + 1) % sunPhases.length;
    const phase = sunPhases[sunPhaseIdx];
    if (!phase) return;
    const elev =
      phase === 'predawn' || phase === 'night'
        ? -20
        : phase === 'morning' || phase === 'evening'
          ? 25
          : 75;
    const azim =
      phase === 'morning' ? 90 : phase === 'noon' ? 180 : phase === 'evening' ? 270 : 200;
    store.appendEvent(
      envelope({
        type: EventType.BUILDING_SUN_POSITION,
        subject: 'building',
        data: {
          phase,
          elevation_degrees: elev,
          azimuth_degrees: azim,
          is_market_open: true,
          trading_date: '2026-05-18',
        },
      }),
    );
  }, 6_000);

  const weatherTimer = window.setInterval(() => {
    weatherIdx = (weatherIdx + 1) % WEATHER_KINDS.length;
    const weather = WEATHER_KINDS[weatherIdx];
    if (!weather) return;
    store.appendEvent(
      envelope({
        type: EventType.BUILDING_WEATHER_CHANGED,
        subject: 'building',
        data: {
          weather_kind: weather,
          previous_weather_kind: WEATHER_KINDS[(weatherIdx + 5) % WEATHER_KINDS.length],
          market_regime:
            weather === 'storm' ? 'bear' : weather === 'sunny' ? 'bull' : 'flat',
          regime_confidence: 0.5 + Math.random() * 0.4,
          frozen_pods_count: 1,
          reduced_pods_count: 1,
        },
      }),
    );
  }, 12_000);

  // ---------- ambient idle actions (every 6 s, random agent) ----------
  const IDLE_PHRASES: Array<{ actor: EventActor; text: string }> = [
    { actor: 'qa', text: 'листает свечные графики' },
    { actor: 'qa', text: 'пересчитывает RSI и MACD' },
    { actor: 'sa', text: 'просматривает ленту РБК' },
    { actor: 'sa', text: 'следит за Twitter-трендами' },
    { actor: 'fa', text: 'сверяет квартальные отчёты' },
    { actor: 'researcher', text: 'копается в SEC-фильтрах' },
    { actor: 'rm', text: 'обновляет лимиты VaR' },
    { actor: 'ea', text: 'тестирует канал брокера' },
    { actor: 'pm', text: 'пьёт кофе и смотрит на heatmap' },
    { actor: 'pm', text: 'обдумывает rebalance' },
  ];
  const idlePodIds = [
    'pod_moex_deepseek_v3',
    'pod_moex_gemini_flash',
    'pod_moex_gpt4omini',
    'pod_moex_qwen_plus',
  ];
  let idleCursor = 0;
  const idleTimer = window.setInterval(() => {
    const phrase = IDLE_PHRASES[idleCursor % IDLE_PHRASES.length];
    const pod = idlePodIds[idleCursor % idlePodIds.length];
    idleCursor++;
    if (!phrase || !pod) return;
    store.appendEvent(
      envelope({
        type: EventType.AGENT_IDLE_ACTION,
        subject: `pod:${pod}`,
        data: {
          actor: phrase.actor,
          text: phrase.text,
          mood: 'idle',
          duration_ms: 4000,
          kind: 'demo_idle',
        },
        sceneHint: { actor: phrase.actor, mood: 'idle' },
      }),
    );
  }, 6_000);

  // ---------- live NAV ticks (every 4 s, all pods) ----------
  const navState = new Map<string, number>();
  for (const pid of idlePodIds) navState.set(pid, 1_000_000);
  const navTimer = window.setInterval(() => {
    for (const pid of idlePodIds) {
      const prev = navState.get(pid) ?? 1_000_000;
      // Random walk +/- 0.15 % per tick.
      const drift = (Math.random() - 0.48) * 0.0015;
      const nav = Math.round(prev * (1 + drift));
      navState.set(pid, nav);
      const sessionOpen = 1_000_000;
      const intradayReturn = (nav - sessionOpen) / sessionOpen;
      store.appendEvent(
        envelope({
          type: EventType.POD_NAV_TICK,
          subject: `pod:${pid}`,
          data: {
            pod_id: pid,
            as_of: new Date().toISOString(),
            nav,
            session_open_nav: sessionOpen,
            intraday_pnl: nav - sessionOpen,
            intraday_return: intradayReturn,
            gross_exposure: 1.0 + Math.random() * 0.2,
            net_exposure: 0.4 + Math.random() * 0.2,
            broker_source: 'demo',
          },
          sceneHint: null,
        }),
      );
    }
  }, 4_000);

  // ---------- occasional dramatic risk-state freeze ----------
  // Every ~45 s we freeze one of the active-floor pods to show off the
  // ice-crack overlay; 6 s later we restore it back to active so the
  // building keeps cycling through the demo without ending up entirely
  // frozen.
  let freezeIdx = 0;
  const freezePool = [
    'pod_moex_deepseek_v3',
    'pod_moex_gpt4omini',
    'pod_moex_qwen_plus',
  ];
  const freezeTimer = window.setInterval(() => {
    const podId = freezePool[freezeIdx % freezePool.length];
    freezeIdx++;
    if (!podId) return;
    store.appendEvent(
      envelope({
        type: EventType.RISK_STATE_CHANGED,
        subject: `pod:${podId}`,
        data: {
          from: 'active',
          to: 'frozen',
          haircut_pct: 1.0,
          reason: 'demo_freeze',
        },
        sceneHint: { actor: 'risk_officer', mood: 'frozen' },
      }),
    );
    window.setTimeout(() => {
      store.appendEvent(
        envelope({
          type: EventType.RISK_STATE_CHANGED,
          subject: `pod:${podId}`,
          data: {
            from: 'frozen',
            to: 'active',
            haircut_pct: 0,
            reason: 'demo_unfreeze',
          },
          sceneHint: { actor: 'risk_officer', mood: 'idle' },
        }),
      );
    }, 6_000);
  }, 45_000);

  // ---------- occasional discipline + centerbook nudges ----------
  let disciplineIdx = 0;
  const disciplineTimer = window.setInterval(() => {
    const pod = idlePodIds[disciplineIdx % idlePodIds.length];
    disciplineIdx++;
    if (!pod) return;
    const severity = (['low', 'medium', 'high', 'critical'] as const)[
      disciplineIdx % 4
    ];
    store.appendEvent(
      envelope({
        type: EventType.DISCIPLINE_FLAG_RAISED,
        subject: `pod:${pod}`,
        data: {
          code: 'suspected_overtrading',
          severity,
          detail: { turnover_z: 2 + Math.random() * 2 },
        },
        sceneHint: { actor: 'discipline_gate', mood: 'worried' },
      }),
    );
    // Center-book pulls back gross right after.
    window.setTimeout(() => {
      store.appendEvent(
        envelope({
          type: EventType.CENTERBOOK_OVERLAY_APPLIED,
          subject: `pod:${pod}`,
          data: {
            pod_scale: 0.75 + Math.random() * 0.2,
            gross_before: 1.1,
            gross_after: 0.85,
          },
          sceneHint: { actor: 'center_book', mood: 'worried' },
        }),
      );
    }, 1_500);
    // PolicyUpdater closes the loop: rotates between no_change /
    // shadow / applied / rolled_back so all four UI states show up
    // in the demo.
    const policyTypes = [
      EventType.POLICY_NO_CHANGE,
      EventType.POLICY_SHADOW_RECOMMENDED,
      EventType.POLICY_APPLIED,
      EventType.POLICY_ROLLED_BACK,
    ] as const;
    const policyType = policyTypes[disciplineIdx % policyTypes.length] ?? EventType.POLICY_NO_CHANGE;
    window.setTimeout(() => {
      store.appendEvent(
        envelope({
          type: policyType,
          subject: `pod:${pod}`,
          data: {
            pod_id: pod,
            trigger_rule: 'discipline_flag_followup',
            param_changed: 'risk_appetite',
            value_after: 'conservative',
            audit_id: 100 + disciplineIdx,
          },
          sceneHint: {
            actor: 'policy_updater',
            mood:
              policyType === EventType.POLICY_ROLLED_BACK
                ? 'proud'
                : policyType === EventType.POLICY_APPLIED
                  ? 'confident'
                  : 'idle',
          },
        }),
      );
    }, 3_000);
  }, 70_000);

  // ---------- per-pod agent chatter ----------
  const cycleTimer = window.setInterval(() => {
    cycle++;
    const podIds = [
      'pod_moex_deepseek_v3',
      'pod_moex_gemini_flash',
      'pod_moex_gpt4omini',
      'pod_moex_qwen_plus',
    ];
    const podId = podIds[cycle % podIds.length];
    if (!podId) return;
    // Each cycle: agents start thinking, emit a signal, then PM decides.
    const traceId = `demo-trace-${cycle}`;
    // 0) cycle.started -- powers the "cycle running" border
    store.appendEvent(
      envelope({
        type: EventType.CYCLE_STARTED,
        subject: `pod:${podId}`,
        traceId,
        data: { pod_id: podId, cycle_hash: traceId },
        sceneHint: { actor: 'system', mood: 'idle' },
      }),
    );
    for (let i = 0; i < AGENTS.length; i++) {
      const agentSpec = AGENTS[i];
      if (!agentSpec) continue;
      window.setTimeout(
        () =>
          store.appendEvent(
            envelope({
              type: EventType.AGENT_THINKING_STARTED,
              subject: `pod:${podId}`,
              traceId,
              data: { actor: agentSpec.actor },
              sceneHint: { actor: agentSpec.actor, mood: 'thinking' },
            }),
          ),
        i * 350,
      );
      window.setTimeout(
        () => {
          const signal = (Math.random() - 0.5) * 2;
          const confidence = 0.4 + Math.random() * 0.5;
          // Map |signal| × confidence to a scene_hint.intensity bucket so
          // the Pixi character animations visibly speed up on dramatic
          // signals.  Most signals stay at "medium" -- only the loud,
          // high-confidence ones escalate.
          const drama = Math.abs(signal) * confidence;
          const intensity =
            drama > 0.85 ? 'critical' : drama > 0.6 ? 'high' : drama > 0.35 ? 'medium' : 'low';
          store.appendEvent(
            envelope({
              type: EventType.AGENT_SIGNAL_EMITTED,
              subject: `pod:${podId}`,
              traceId,
              data: {
                actor: agentSpec.actor,
                signal,
                confidence,
                top_tickers: pickRandom(TICKERS, 3),
                latency_ms: 800 + Math.random() * 1200,
              },
              sceneHint: { actor: agentSpec.actor, mood: agentSpec.mood, intensity },
            }),
          );
        },
        i * 350 + 1_400,
      );
    }
    // PM decides ~3s after the first agent.
    window.setTimeout(() => {
      // Build a plausible portfolio: 4-6 tickers with mixed long/short
      // weights summing roughly to target_gross.
      const portfolioCount = 4 + Math.floor(Math.random() * 3);
      const tickers = pickRandom(TICKERS, portfolioCount);
      const weights = tickers.map((t) => ({
        ticker: t,
        weight: (Math.random() * 0.18 + 0.04) * (Math.random() < 0.75 ? 1 : -1),
      }));
      store.appendEvent(
        envelope({
          type: EventType.PM_DECISION_MADE,
          subject: `pod:${podId}`,
          traceId,
          data: {
            ui_summary: `Cycle ${cycle}: rotate toward ${pickRandom(TICKERS, 2).join(' + ')}`,
            confidence: 0.55 + Math.random() * 0.3,
            target_gross: 0.8 + Math.random() * 0.4,
            intents_count: Math.floor(2 + Math.random() * 6),
            top_weights: weights,
          },
          sceneHint: { actor: 'pm', mood: 'confident' },
        }),
      );
    }, 3_200);

    // Execution agent fires 2-4 orders ~4s after the decision -- the
    // resulting ticker rail chips show the trade flow.
    window.setTimeout(() => {
      const orderCount = 2 + Math.floor(Math.random() * 3);
      const tickers = pickRandom(TICKERS, orderCount);
      tickers.forEach((ticker, idx) => {
        window.setTimeout(() => {
          const side = Math.random() < 0.5 ? 'buy' : 'sell';
          const price = (100 + Math.random() * 4000).toFixed(2);
          store.appendEvent(
            envelope({
              type: EventType.ORDER_SUBMITTED,
              subject: `pod:${podId}`,
              traceId,
              data: { ticker, side, price, qty: Math.floor(50 + Math.random() * 500) },
              sceneHint: { actor: 'ea', mood: 'confident' },
            }),
          );
          // 600 ms later, fill or reject (~85% fills).
          window.setTimeout(() => {
            const filled = Math.random() < 0.85;
            store.appendEvent(
              envelope({
                type: filled ? EventType.ORDER_FILLED : EventType.ORDER_REJECTED,
                subject: `pod:${podId}`,
                traceId,
                data: filled
                  ? { ticker, side, price, fill_ratio: 1.0 }
                  : { ticker, side, reason: 'risk_limit' },
                sceneHint: { actor: 'ea', mood: filled ? 'confident' : 'angry' },
              }),
            );
            // On a fill, also emit a position event so the live book
            // updates between PM decisions.  Most fills open or
            // adjust an existing position; occasionally close a flat.
            if (filled) {
              const weight = (Math.random() * 0.12 + 0.03) * (side === 'sell' ? -1 : 1);
              const shouldClose = Math.random() < 0.1;
              store.appendEvent(
                envelope({
                  type: shouldClose ? EventType.POSITION_CLOSED : EventType.POSITION_OPENED,
                  subject: `pod:${podId}`,
                  traceId,
                  data: shouldClose
                    ? { ticker, reason: 'flat_out' }
                    : { ticker, weight, qty: Math.floor(50 + Math.random() * 500) },
                  sceneHint: { actor: 'ea', mood: 'idle' },
                }),
              );
            }
          }, 600);
        }, idx * 400);
      });
    }, 4_500);

    // Cycle completed ~8 s in (after the last order fires).
    window.setTimeout(() => {
      store.appendEvent(
        envelope({
          type: EventType.CYCLE_COMPLETED,
          subject: `pod:${podId}`,
          traceId,
          data: {
            cycle_hash: traceId,
            intents: 4,
            executed: 3,
            approved: true,
          },
          sceneHint: { actor: 'system', mood: 'idle' },
        }),
      );
    }, 8_000);
    // Utility recorded ~9 s in -- a plausible component breakdown so
    // the stacked-bar chart in StatsPanel gets fresh data each cycle.
    // alpha is the only positive contribution; everything else is a
    // drag (downside / turnover / inactivity / concentration /
    // execution_rejection / risk_state_penalty).
    window.setTimeout(() => {
      const alpha = 0.04 + Math.random() * 0.06; // 0.04 .. 0.10
      const downside = -(0.005 + Math.random() * 0.025); // -0.005 .. -0.03
      const turnover = -(0.002 + Math.random() * 0.015); // -0.002 .. -0.017
      const inactivity = -(Math.random() * 0.004); // 0 .. -0.004
      const concentration = -(Math.random() * 0.008); // 0 .. -0.008
      const executionRejection = -(Math.random() * 0.003);
      const riskStatePenalty = -(Math.random() * 0.005);
      const total =
        alpha +
        downside +
        turnover +
        inactivity +
        concentration +
        executionRejection +
        riskStatePenalty;
      store.appendEvent(
        envelope({
          type: EventType.UTILITY_RECORDED,
          subject: `pod:${podId}`,
          traceId,
          data: {
            pod_id: podId,
            total,
            version: 'v1',
            components: {
              alpha,
              downside,
              turnover,
              inactivity,
              concentration,
              execution_rejection: executionRejection,
              risk_state_penalty: riskStatePenalty,
            },
          },
          sceneHint: { actor: 'system', mood: 'idle' },
        }),
      );
    }, 9_000);
  }, 9_000);

  // Fire one batch right away so the first paint shows live activity.
  window.setTimeout(() => {
    store.appendEvent(
      envelope({
        type: EventType.BUILDING_SUN_POSITION,
        subject: 'building',
        data: {
          phase: 'noon',
          elevation_degrees: 75,
          azimuth_degrees: 180,
          is_market_open: true,
          trading_date: '2026-05-18',
        },
      }),
    );
    store.appendEvent(
      envelope({
        type: EventType.BUILDING_WEATHER_CHANGED,
        subject: 'building',
        data: {
          weather_kind: 'sunny',
          previous_weather_kind: null,
          market_regime: 'bull',
          regime_confidence: 0.82,
          frozen_pods_count: 1,
          reduced_pods_count: 1,
        },
      }),
    );
  }, 100);

  // Seed the leaderboard ~25 s in so the empty "Awaiting Friday
  // compute…" placeholder doesn't sit forever in the demo.  Then
  // every ~35 s, fire a rank_changed event so the up/down arrow
  // animations are visible without waiting for Friday.
  window.setTimeout(() => {
    store.appendEvent(
      envelope({
        type: EventType.LEADERBOARD_COMPUTED,
        subject: 'building',
        data: {
          period: 'weekly',
          as_of: new Date().toISOString(),
          metric: 'utility_avg_7d',
          rows: [
            { pod_id: 'pod_moex_deepseek_v3', rank: 1, value: 0.187, name: 'DeepSeek V3 Pod' },
            { pod_id: 'pod_moex_gpt4omini', rank: 2, value: 0.094, name: 'GPT-4o-mini Pod' },
            { pod_id: 'pod_moex_qwen_plus', rank: 3, value: 0.061, name: 'Qwen Plus Pod' },
            { pod_id: 'pod_moex_gemini_flash', rank: 4, value: 0.028, name: 'Gemini Flash Pod' },
            { pod_id: 'pod_moex_grok_fast', rank: 5, value: -0.012, name: 'Grok Fast Pod' },
          ],
        },
        sceneHint: { actor: 'leaderboard_keeper', mood: 'idle' },
      }),
    );
  }, 25_000);
  let rankShuffleIdx = 0;
  const rankShuffleTimer = window.setInterval(() => {
    rankShuffleIdx++;
    // Pick two pods to swap, alternating which pair moves.
    const swaps: Array<[string, number, number]> = rankShuffleIdx % 2 === 0
      ? [
          ['pod_moex_gpt4omini', 2, 3],
          ['pod_moex_qwen_plus', 3, 2],
        ]
      : [
          ['pod_moex_gemini_flash', 4, 2],
          ['pod_moex_gpt4omini', 2, 4],
        ];
    for (const [podId, fromRank, toRank] of swaps) {
      store.appendEvent(
        envelope({
          type: EventType.LEADERBOARD_RANK_CHANGED,
          subject: 'building',
          data: {
            pod_id: podId,
            from_rank: fromRank,
            to_rank: toRank,
            value: 0.05 + Math.random() * 0.1,
            metric: 'utility_avg_7d',
            as_of: new Date().toISOString(),
          },
          sceneHint: { actor: 'leaderboard_keeper', mood: toRank < fromRank ? 'confident' : 'worried' },
        }),
      );
    }
  }, 35_000);

  // Showcase a floor transition ~50 s in: Grok Fast pod gets
  // promoted from the reduced floor up to active offices.  The
  // building view paints a yellow "moving" chevron on the pod
  // window between ``room.exited`` and ``room.entered``.
  window.setTimeout(() => {
    const podId = 'pod_moex_grok_fast';
    store.appendEvent(
      envelope({
        type: EventType.ROOM_EXITED,
        subject: `pod:${podId}`,
        data: { pod_id: podId, room: 'reduced_floor' },
        sceneHint: { actor: 'system', mood: 'idle' },
      }),
    );
    window.setTimeout(() => {
      store.appendEvent(
        envelope({
          type: EventType.ROOM_ENTERED,
          subject: `pod:${podId}`,
          data: { pod_id: podId, room: 'active_offices' },
          sceneHint: { actor: 'system', mood: 'confident' },
        }),
      );
    }, 2_500);
  }, 50_000);

  // Showcase the daily-close night ritual ~75 s in so visitors see it
  // without waiting forever.  ``started`` banner sits up top until
  // ``completed`` fires 8 s later with a pretend net-PnL summary,
  // then the completed banner auto-dismisses after 30 s.  Visitors
  // can re-run with ``window.__demo.dailyClose()``.
  window.setTimeout(() => {
    store.appendEvent(
      envelope({
        type: EventType.BUILDING_DAILY_CLOSE_STARTED,
        subject: 'building',
        data: { trading_date: '2026-05-18', closing_time: new Date().toISOString() },
        sceneHint: { actor: 'system', mood: 'worried' },
      }),
    );
    // Mid-close: center-book reallocates capital -- promote winners,
    // trim laggards.  Each pod gets a single ``allocation_changed``
    // event so the Stats panel's Capital row blinks with the new
    // number + colour-coded delta.
    const reallocations: Array<[string, number, number, string]> = [
      ['pod_moex_deepseek_v3', 1_000_000, 1_300_000, 'leader_promotion'],
      ['pod_moex_gpt4omini', 1_000_000, 1_100_000, 'consistent_alpha'],
      ['pod_moex_qwen_plus', 1_000_000, 950_000, 'rebalance'],
      ['pod_moex_gemini_flash', 1_000_000, 850_000, 'underperformance'],
      ['pod_moex_grok_fast', 800_000, 600_000, 'reduce_exposure'],
    ];
    reallocations.forEach(([podId, before, after, reason], idx) => {
      window.setTimeout(() => {
        store.appendEvent(
          envelope({
            type: EventType.CENTERBOOK_ALLOCATION_CHANGED,
            subject: `pod:${podId}`,
            data: {
              pod_id: podId,
              capital_before: before,
              capital_after: after,
              reason,
            },
            sceneHint: { actor: 'center_book', mood: after > before ? 'proud' : 'worried' },
          }),
        );
      }, 2_000 + idx * 800);
    });
    window.setTimeout(() => {
      store.appendEvent(
        envelope({
          type: EventType.BUILDING_DAILY_CLOSE_COMPLETED,
          subject: 'building',
          data: {
            trading_date: '2026-05-18',
            pods_closed: 5,
            total_pnl: 12_345_000,
            top_pod_name: 'DeepSeek V3 Pod',
          },
          sceneHint: { actor: 'system', mood: 'proud' },
        }),
      );
    }, 8_000);
  }, 75_000);

  // Showcase the ice-crack animation about 4 s in so visitors see the
  // freeze effect without waiting for the next 45 s cycle.
  window.setTimeout(() => {
    store.appendEvent(
      envelope({
        type: EventType.RISK_STATE_CHANGED,
        subject: 'pod:pod_moex_gemini_flash',
        data: {
          from: 'active',
          to: 'frozen',
          haircut_pct: 1.0,
          reason: 'demo_initial_freeze',
        },
        sceneHint: { actor: 'risk_officer', mood: 'frozen' },
      }),
    );
    window.setTimeout(() => {
      store.appendEvent(
        envelope({
          type: EventType.RISK_STATE_CHANGED,
          subject: 'pod:pod_moex_gemini_flash',
          data: {
            from: 'frozen',
            to: 'active',
            haircut_pct: 0,
            reason: 'demo_initial_unfreeze',
          },
          sceneHint: { actor: 'risk_officer', mood: 'idle' },
        }),
      );
    }, 4_500);
  }, 4_000);

  return {
    stop: () => {
      window.clearInterval(sunTimer);
      window.clearInterval(weatherTimer);
      window.clearInterval(cycleTimer);
      window.clearInterval(freezeTimer);
      window.clearInterval(idleTimer);
      window.clearInterval(navTimer);
      window.clearInterval(disciplineTimer);
      window.clearInterval(rankShuffleTimer);
      demoStarted = false;
    },
  };
}

function envelope({
  type,
  subject,
  data,
  sceneHint = null,
  traceId,
}: {
  type: string;
  subject: string;
  data: Record<string, unknown>;
  sceneHint?: Record<string, unknown> | null;
  traceId?: string;
}): AnyEnvelope {
  return {
    id: `demo-${Math.random().toString(36).slice(2, 11)}`,
    ts: new Date().toISOString(),
    subject,
    type,
    version: 1,
    trace_id: traceId ?? 'demo',
    data,
    scene_hint: sceneHint,
  };
}

function pickRandom<T>(arr: readonly T[], n: number): T[] {
  const copy = arr.slice();
  const out: T[] = [];
  for (let i = 0; i < n && copy.length > 0; i++) {
    const idx = Math.floor(Math.random() * copy.length);
    const item = copy.splice(idx, 1)[0];
    if (item !== undefined) out.push(item);
  }
  return out;
}

export function isDemoModeRequested(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('demo') === '1';
  } catch {
    return false;
  }
}
